import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Button,
  TextField,
  Tabs,
  Tab,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  Divider,
  Stack,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Add,
  Delete,
  Edit,
  CheckCircle,
  Cancel,
  Business,
  AttachMoney,
  Inventory,
  VerifiedUser,
  Warning,
  Refresh,
  DataUsage
} from '@mui/icons-material';
import {
  getTenant,
  updateTenant,
  getTenantProducts,
  createTenantProduct,
  updateTenantProduct,
  deleteTenantProduct
} from '@/services/api/tenantService';
import { getToken } from '@/utils/storage';
import type {
  Tenant,
  TenantUpdate,
  TenantBilling,
  TenantLimits,
  Product,
  TenantProduct,
  TenantProductCreate,
  TenantProductUpdate,
  RevenueRecipient
} from '@types';

const API_BASE = import.meta.env.VITE_API_URL || 'https://ninshousystem-api-361153976392.asia-northeast1.run.app';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const TenantDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tenantId = parseInt(id || '0', 10);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);

  // テナント情報
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [formData, setFormData] = useState<TenantUpdate>({});

  // 課金設定
  const [billing, setBilling] = useState<TenantBilling>({
    model: 'revenue_share',
    our_share_percent: 30,
    partner_share_percent: 70,
    monthly_minimum: 0,
    per_user_fee: 0
  });

  // 機能制限
  const [limits, setLimits] = useState<TenantLimits>({
    max_products: 10,
    max_users: 1000,
    max_api_calls_per_month: 100000,
    features: []
  });

  // プロダクト一覧
  const [products, setProducts] = useState<Product[]>([]);
  const [tenantProducts, setTenantProducts] = useState<TenantProduct[]>([]);

  // ドメイン検証
  const [domainVerifying, setDomainVerifying] = useState(false);
  const [domainVerified, setDomainVerified] = useState(false);
  const [domainError, setDomainError] = useState('');

  // API使用量
  const [usage, setUsage] = useState<{
    total: number;
    by_category: Record<string, number>;
    year_month: string;
    limit: { max: number; usage_percent: number; warning: boolean };
  } | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  // プロダクト追加ダイアログ
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<TenantProduct | null>(null);
  const [productForm, setProductForm] = useState<{
    product_id: number;
    is_enabled: number;
    allowed_plans: string[];
    recipients: RevenueRecipient[];
  }>({
    product_id: 0,
    is_enabled: 1,
    allowed_plans: [],
    recipients: [
      { name: '運営', email: 'admin@example.com', percent: 30 },
      { name: 'パートナー', email: '', percent: 70 }
    ]
  });

  // 利用可能な機能一覧
  const AVAILABLE_FEATURES = ['webhook', 'custom_domain', 'api_access', 'white_label', 'custom_smtp'];

  useEffect(() => {
    if (tenantId) {
      fetchData();
    }
  }, [tenantId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tenantData, tenantProductsData] = await Promise.all([
        getTenant(tenantId),
        getTenantProducts(tenantId)
      ]);

      setTenant(tenantData);
      setFormData({
        name: tenantData.name,
        owner_email: tenantData.owner_email,
        owner_name: tenantData.owner_name || '',
        subdomain: tenantData.subdomain || '',
        custom_domain: tenantData.custom_domain || '',
        plan: tenantData.plan
      });

      if (tenantData.billing) {
        setBilling(tenantData.billing);
      }
      if (tenantData.limits) {
        setLimits(tenantData.limits);
      }

      // ドメイン検証状態を設定
      setDomainVerified(tenantData.domain_verified === 1);

      setTenantProducts(tenantProductsData.items);

      // プロダクト一覧と使用量を取得
      await Promise.all([fetchProducts(), fetchUsage()]);
    } catch (err) {
      setError('テナント情報の取得に失敗しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${API_BASE}/products`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  const fetchUsage = async () => {
    try {
      setUsageLoading(true);
      const res = await fetch(`${API_BASE}/admin/tenants/${tenantId}/usage`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsage(data);
      }
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    } finally {
      setUsageLoading(false);
    }
  };

  const verifyDomain = async () => {
    try {
      setDomainVerifying(true);
      setDomainError('');
      const res = await fetch(`${API_BASE}/admin/tenants/${tenantId}/verify-domain`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await res.json();

      if (data.verified) {
        setDomainVerified(true);
        setSuccess('ドメイン検証が成功しました');
      } else {
        setDomainVerified(false);
        setDomainError(data.error || 'ドメイン検証に失敗しました');
      }
    } catch (err) {
      setDomainError('ドメイン検証中にエラーが発生しました');
      console.error('Domain verification failed:', err);
    } finally {
      setDomainVerifying(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const updateData: TenantUpdate = {
        ...formData,
        billing,
        limits
      };

      await updateTenant(tenantId, updateData);
      setSuccess('テナント情報を更新しました');
      fetchData();
    } catch (err) {
      setError('保存に失敗しました');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setProductForm({
      product_id: 0,
      is_enabled: 1,
      allowed_plans: [],
      recipients: [
        { name: '運営', email: 'admin@example.com', percent: 30 },
        { name: 'パートナー', email: tenant?.owner_email || '', percent: 70 }
      ]
    });
    setProductDialogOpen(true);
  };

  const handleEditProduct = (tp: TenantProduct) => {
    setEditingProduct(tp);
    const rawRecipients = tp.billing?.recipients || [];
    const recipients: RevenueRecipient[] = rawRecipients.length > 0
      ? rawRecipients.map(r => ({
          name: r.name || '',
          email: r.email || '',
          percent: r.percent || 0
        }))
      : [
          { name: '運営', email: 'admin@example.com', percent: 30 },
          { name: 'パートナー', email: tenant?.owner_email || '', percent: 70 }
        ];
    setProductForm({
      product_id: tp.product_id,
      is_enabled: tp.is_enabled,
      allowed_plans: tp.allowed_plans || [],
      recipients
    });
    setProductDialogOpen(true);
  };

  const handleSaveProduct = async () => {
    try {
      const totalPercent = productForm.recipients.reduce((sum, r) => sum + r.percent, 0);
      if (Math.abs(totalPercent - 100) > 0.01) {
        setError(`収益分配の合計は100%である必要があります（現在: ${totalPercent}%）`);
        return;
      }

      const data: TenantProductCreate | TenantProductUpdate = {
        is_enabled: productForm.is_enabled,
        allowed_plans: productForm.allowed_plans,
        billing: {
          model: 'revenue_share',
          recipients: productForm.recipients
        }
      };

      if (editingProduct) {
        await updateTenantProduct(tenantId, editingProduct.product_id, data);
        setSuccess('プロダクト設定を更新しました');
      } else {
        await createTenantProduct(tenantId, {
          ...data,
          product_id: productForm.product_id
        } as TenantProductCreate);
        setSuccess('プロダクトを追加しました');
      }

      setProductDialogOpen(false);
      fetchData();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setError(error.response?.data?.detail || '保存に失敗しました');
    }
  };

  const handleDeleteProduct = async (productId: number) => {
    if (!confirm('このプロダクト設定を削除しますか？')) return;
    try {
      await deleteTenantProduct(tenantId, productId);
      setSuccess('プロダクト設定を削除しました');
      fetchData();
    } catch (err) {
      setError('削除に失敗しました');
    }
  };

  const handleRecipientChange = (index: number, field: keyof RevenueRecipient, value: string | number) => {
    const newRecipients = [...productForm.recipients];
    newRecipients[index] = { ...newRecipients[index], [field]: value } as RevenueRecipient;
    setProductForm({ ...productForm, recipients: newRecipients });
  };

  const addRecipient = () => {
    setProductForm({
      ...productForm,
      recipients: [...productForm.recipients, { name: '', email: '', percent: 0 }]
    });
  };

  const removeRecipient = (index: number) => {
    const newRecipients = productForm.recipients.filter((_, i) => i !== index);
    setProductForm({ ...productForm, recipients: newRecipients });
  };

  const toggleFeature = (feature: string) => {
    const currentFeatures = limits.features || [];
    if (currentFeatures.includes(feature)) {
      setLimits({ ...limits, features: currentFeatures.filter(f => f !== feature) });
    } else {
      setLimits({ ...limits, features: [...currentFeatures, feature] });
    }
  };

  // プロダクトのプラン一覧を取得
  const getProductPlans = (productId: number): string[] => {
    const product = products.find(p => p.id === productId);
    return product?.plans?.map(p => p.code) || [];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tenant) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">テナントが見つかりません</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/tenants')} sx={{ mt: 2 }}>
          一覧に戻る
        </Button>
      </Box>
    );
  }

  // 未追加のプロダクト一覧
  const availableProducts = products.filter(
    p => !tenantProducts.find(tp => tp.product_id === p.id)
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton onClick={() => navigate('/admin/tenants')}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight="bold">
            {tenant.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            テナントコード: {tenant.code}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? '保存中...' : '保存'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab icon={<Business />} label="基本情報" iconPosition="start" />
            <Tab icon={<AttachMoney />} label="課金・制限" iconPosition="start" />
            <Tab icon={<Inventory />} label="プロダクト・収益分配" iconPosition="start" />
          </Tabs>
        </Box>

        {/* 基本情報タブ */}
        <TabPanel value={tabValue} index={0}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="テナント名"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>プラン</InputLabel>
                  <Select
                    value={formData.plan || 'basic'}
                    label="プラン"
                    onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                  >
                    <MenuItem value="basic">Basic</MenuItem>
                    <MenuItem value="pro">Pro</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="オーナーメール"
                  type="email"
                  value={formData.owner_email || ''}
                  onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="オーナー名"
                  value={formData.owner_name || ''}
                  onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="サブドメイン"
                  value={formData.subdomain || ''}
                  onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
                  fullWidth
                  helperText="例: acme → acme.license.example.com"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    label="カスタムドメイン"
                    value={formData.custom_domain || ''}
                    onChange={(e) => setFormData({ ...formData, custom_domain: e.target.value })}
                    fullWidth
                    helperText="例: license.acme.com"
                    InputProps={{
                      endAdornment: formData.custom_domain && (
                        <Tooltip title={domainVerified ? 'DNS検証済み' : 'DNS未検証'}>
                          {domainVerified ? (
                            <VerifiedUser color="success" />
                          ) : (
                            <Warning color="warning" />
                          )}
                        </Tooltip>
                      )
                    }}
                  />
                  {formData.custom_domain && (
                    <Button
                      variant="outlined"
                      onClick={verifyDomain}
                      disabled={domainVerifying}
                      sx={{ mt: 1, minWidth: 100 }}
                    >
                      {domainVerifying ? <CircularProgress size={20} /> : 'DNS検証'}
                    </Button>
                  )}
                </Box>
                {domainError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {domainError}
                  </Alert>
                )}
                {formData.custom_domain && !domainVerified && !domainError && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    CNAMEレコードを <code>cname.vercel-dns.com</code> に設定してください
                  </Alert>
                )}
              </Grid>
            </Grid>

            {/* API使用量セクション */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DataUsage /> API使用量
              <Tooltip title="使用量を更新">
                <IconButton size="small" onClick={fetchUsage} disabled={usageLoading}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Typography>
            {usageLoading ? (
              <CircularProgress size={24} />
            ) : usage ? (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {usage.year_month} の使用量
                    </Typography>
                    <Typography variant="h4">
                      {usage.total.toLocaleString()} 回
                    </Typography>
                    {usage.limit.max > 0 && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          上限: {usage.limit.max.toLocaleString()} 回 ({usage.limit.usage_percent.toFixed(1)}%)
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(usage.limit.usage_percent, 100)}
                          color={usage.limit.warning ? 'warning' : 'primary'}
                          sx={{ mt: 1, height: 8, borderRadius: 1 }}
                        />
                      </>
                    )}
                    {usage.limit.max === -1 && (
                      <Typography variant="body2" color="text.secondary">
                        上限: 無制限
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      カテゴリ別内訳
                    </Typography>
                    {Object.entries(usage.by_category).map(([category, count]) => (
                      <Box key={category} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">{category}</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {count.toLocaleString()} 回
                        </Typography>
                      </Box>
                    ))}
                    {Object.keys(usage.by_category).length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        まだAPI呼び出しがありません
                      </Typography>
                    )}
                  </Grid>
                </Grid>
                {usage.limit.warning && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    API使用量が上限の80%を超えています。プランのアップグレードをご検討ください。
                  </Alert>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                使用量データがありません
              </Typography>
            )}
          </CardContent>
        </TabPanel>

        {/* 課金・制限タブ */}
        <TabPanel value={tabValue} index={1}>
          <CardContent>
            <Typography variant="h6" gutterBottom>課金設定</Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>課金モデル</InputLabel>
                  <Select
                    value={billing.model || 'revenue_share'}
                    label="課金モデル"
                    onChange={(e) => setBilling({ ...billing, model: e.target.value })}
                  >
                    <MenuItem value="revenue_share">レベニューシェア</MenuItem>
                    <MenuItem value="fixed">固定費</MenuItem>
                    <MenuItem value="license">ライセンス</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="運営側シェア (%)"
                  type="number"
                  value={billing.our_share_percent || 0}
                  onChange={(e) => setBilling({ ...billing, our_share_percent: Number(e.target.value) })}
                  fullWidth
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="パートナーシェア (%)"
                  type="number"
                  value={billing.partner_share_percent || 0}
                  onChange={(e) => setBilling({ ...billing, partner_share_percent: Number(e.target.value) })}
                  fullWidth
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="月額最低料金"
                  type="number"
                  value={billing.monthly_minimum || 0}
                  onChange={(e) => setBilling({ ...billing, monthly_minimum: Number(e.target.value) })}
                  fullWidth
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="ユーザーあたり料金"
                  type="number"
                  value={billing.per_user_fee || 0}
                  onChange={(e) => setBilling({ ...billing, per_user_fee: Number(e.target.value) })}
                  fullWidth
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>機能制限</Typography>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="最大プロダクト数"
                  type="number"
                  value={limits.max_products || 0}
                  onChange={(e) => setLimits({ ...limits, max_products: Number(e.target.value) })}
                  fullWidth
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="最大ユーザー数"
                  type="number"
                  value={limits.max_users || 0}
                  onChange={(e) => setLimits({ ...limits, max_users: Number(e.target.value) })}
                  fullWidth
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="月間API呼び出し上限"
                  type="number"
                  value={limits.max_api_calls_per_month || 0}
                  onChange={(e) => setLimits({ ...limits, max_api_calls_per_month: Number(e.target.value) })}
                  fullWidth
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" gutterBottom>利用可能機能</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {AVAILABLE_FEATURES.map(feature => (
                <Chip
                  key={feature}
                  label={feature}
                  color={(limits.features || []).includes(feature) ? 'primary' : 'default'}
                  onClick={() => toggleFeature(feature)}
                  icon={(limits.features || []).includes(feature) ? <CheckCircle /> : <Cancel />}
                />
              ))}
            </Box>
          </CardContent>
        </TabPanel>

        {/* プロダクト・収益分配タブ */}
        <TabPanel value={tabValue} index={2}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">販売プロダクト設定</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddProduct}
                disabled={availableProducts.length === 0}
              >
                プロダクト追加
              </Button>
            </Box>

            {tenantProducts.length === 0 ? (
              <Alert severity="info">
                プロダクトが設定されていません。「プロダクト追加」から販売するプロダクトを設定してください。
              </Alert>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>プロダクト</TableCell>
                      <TableCell>ステータス</TableCell>
                      <TableCell>販売可能プラン</TableCell>
                      <TableCell>収益分配</TableCell>
                      <TableCell align="right">操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tenantProducts.map((tp) => (
                      <TableRow key={tp.id}>
                        <TableCell>
                          <Typography fontWeight="medium">{tp.product_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {tp.product_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={tp.is_enabled ? '有効' : '無効'}
                            color={tp.is_enabled ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {tp.allowed_plans && tp.allowed_plans.length > 0 ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                              {tp.allowed_plans.map(plan => (
                                <Chip key={plan} label={plan} size="small" variant="outlined" />
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">全プラン</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {tp.billing?.recipients?.map((r, i) => (
                            <Typography key={i} variant="body2">
                              {r.name}: {r.percent}%
                            </Typography>
                          ))}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="編集">
                            <IconButton size="small" onClick={() => handleEditProduct(tp)}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="削除">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteProduct(tp.product_id)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </TabPanel>
      </Card>

      {/* プロダクト追加/編集ダイアログ */}
      <Dialog open={productDialogOpen} onClose={() => setProductDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProduct ? 'プロダクト設定を編集' : 'プロダクトを追加'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {!editingProduct && (
              <FormControl fullWidth>
                <InputLabel>プロダクト</InputLabel>
                <Select
                  value={productForm.product_id}
                  label="プロダクト"
                  onChange={(e) => setProductForm({
                    ...productForm,
                    product_id: Number(e.target.value),
                    allowed_plans: []
                  })}
                >
                  {availableProducts.map(p => (
                    <MenuItem key={p.id} value={p.id}>{p.name} ({p.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={productForm.is_enabled === 1}
                  onChange={(e) => setProductForm({
                    ...productForm,
                    is_enabled: e.target.checked ? 1 : 0
                  })}
                />
              }
              label="販売を有効にする"
            />

            {productForm.product_id > 0 && (
              <>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>販売可能プラン（空の場合は全プラン）</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {getProductPlans(productForm.product_id).map(plan => (
                      <Chip
                        key={plan}
                        label={plan}
                        color={productForm.allowed_plans.includes(plan) ? 'primary' : 'default'}
                        onClick={() => {
                          if (productForm.allowed_plans.includes(plan)) {
                            setProductForm({
                              ...productForm,
                              allowed_plans: productForm.allowed_plans.filter(p => p !== plan)
                            });
                          } else {
                            setProductForm({
                              ...productForm,
                              allowed_plans: [...productForm.allowed_plans, plan]
                            });
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                <Divider />

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2">収益分配設定</Typography>
                    <Button size="small" startIcon={<Add />} onClick={addRecipient}>
                      分配先追加
                    </Button>
                  </Box>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    分配率の合計は100%になる必要があります
                    （現在: {productForm.recipients.reduce((sum, r) => sum + r.percent, 0)}%）
                  </Alert>

                  {productForm.recipients.map((recipient, index) => (
                    <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                      <Grid item xs={3}>
                        <TextField
                          label="分配先名"
                          value={recipient.name}
                          onChange={(e) => handleRecipientChange(index, 'name', e.target.value)}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          label="メールアドレス"
                          type="email"
                          value={recipient.email}
                          onChange={(e) => handleRecipientChange(index, 'email', e.target.value)}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          label="分配率 (%)"
                          type="number"
                          value={recipient.percent}
                          onChange={(e) => handleRecipientChange(index, 'percent', Number(e.target.value))}
                          fullWidth
                          size="small"
                          inputProps={{ min: 0, max: 100 }}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <IconButton
                          color="error"
                          onClick={() => removeRecipient(index)}
                          disabled={productForm.recipients.length <= 1}
                        >
                          <Delete />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleSaveProduct}
            disabled={!editingProduct && productForm.product_id === 0}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
